import { useMemoSelector } from '@spa/redux/hooks'
import { getCurrentLocationAndTeam } from '@spa/scenes/Plannings/redux/selectors/entities/teams/getCurrentLocationAndTeam/getCurrentLocationAndTeam'
import { useParams } from 'react-router-dom'
import { getCurrentAccountId } from 'snap-redux/modules/accounts/selectors'
import { getCurrentMembershipSelector } from 'snap-redux/modules/memberships/selectors'

import { trackerScheduleFeaturesInteractionEvent } from '../events'
import type {
  ScheduleFeatureActionName,
  ScheduleFeatureAdditionalData,
  ScheduleFeatureChangeTimeRangeWithArrowType,
  ScheduleFeatureDragAndDropActionName,
  ScheduleFeatureDragAndDropComponent,
  ScheduleFeaturePublishScheduleActionName,
  ScheduleFeatureRestCreationInBulkContractsWithShiftsDeletionRequestedCount,
  ScheduleFeatureRestCreationInBulkContractsWithShiftsToDeleteCount,
  ScheduleFeatureScheduleElementCardComponent,
  ScheduleFeatureShiftPlanificationType,
  ScheduleFeatureShiftStatus,
  ScheduleFeaturesProperties,
  ScheduleFeaturesShortcutActionName,
  ScheduleFeaturesShortcutTargetComponent,
  ScheduleFeatureView
} from '../types'

export const useTrackScheduleFeaturesInteraction = () => {
  const accountId = useMemoSelector(getCurrentAccountId)
  const membership = useMemoSelector(getCurrentMembershipSelector)
  const currentLocationAndTeam = useMemoSelector(getCurrentLocationAndTeam)
  const queryParams = useParams<any>()
  const viewType = queryParams.view || 'week'

  const account_id = Number(accountId)
  const planning_level = currentLocationAndTeam.currentTeam
    ? 'team'
    : 'location'
  const planning_view = viewType as ScheduleFeatureView
  const membership_role = membership.role
  const location_id = currentLocationAndTeam.currentLocation?.id
  const team_id = currentLocationAndTeam.currentTeam?.id

  const trackInteraction = (
    action_name: ScheduleFeatureActionName,
    additional_data?: ScheduleFeatureAdditionalData
  ) => {
    const properties = {
      account_id,
      action_name,
      planning_level,
      planning_view,
      membership_role,
      location_id,
      team_id,
      additional_data
    } as ScheduleFeaturesProperties

    trackerScheduleFeaturesInteractionEvent(properties)
  }

  return {
    toolsOpened: () => trackInteraction('tools_opened'),
    alertListOpened: () => trackInteraction('alert_list_opened'),
    displaySettingsMenuOpened: () =>
      trackInteraction('display_settings_menu_opened'),
    shortcutsRecapOpened: () => trackInteraction('shortcuts_recap_opened'),
    viewChanged: () => trackInteraction('view_changed'),
    analysisPanelOpened: () => trackInteraction('analysis_panel_opened'),
    publishScheduleMenuOpened: (
      publish_schedule_action_name: ScheduleFeaturePublishScheduleActionName
    ) =>
      trackInteraction('publish_schedule_menu_opened', {
        publish_schedule_action_name
      }),
    employeeCreationModalOpened: () =>
      trackInteraction('employee_creation_modal_opened'),
    countersParametersMenuOpened: () =>
      trackInteraction('counters_parameters_menu_opened'),
    changeTimeRangeWithArrowPerformed: (
      change_time_range_with_arrow_type: ScheduleFeatureChangeTimeRangeWithArrowType
    ) =>
      trackInteraction('change_time_range_with_arrow_performed', {
        change_time_range_with_arrow_type
      }),
    calendarNavigationDayChanged: () =>
      trackInteraction('calendar_navigation_day_changed'),
    employeeSearchInputFieldOpened: () =>
      trackInteraction('employee_search_input_field_opened'),
    teamLocationChanged: () => trackInteraction('team_location_changed'),
    employeeMemberpageOpened: () =>
      trackInteraction('employee_memberpage_opened'),
    dayNoteMenuOpened: () => trackInteraction('day_note_menu_opened'),
    dragAndDropScheduleElementPerformed: (
      drag_and_drop_action_name: ScheduleFeatureDragAndDropActionName,
      drag_and_drop_component: ScheduleFeatureDragAndDropComponent
    ) =>
      trackInteraction('drag_and_drop_schedule_element_performed', {
        drag_and_drop_action_name,
        drag_and_drop_component
      }),
    scheduleElementCreationMenuOpened: () =>
      trackInteraction('schedule_element_creation_menu_opened'),
    scheduleElementCardOpened: (
      schedule_element_card_component: ScheduleFeatureScheduleElementCardComponent,
      schedule_element_card_shift_planification_type?: ScheduleFeatureShiftPlanificationType,
      shift_status_data?: {
        published: boolean
        declared: boolean
        validated: boolean
      }
    ) => {
      const schedule_element_card_shift_status =
        determineShiftStatus(shift_status_data)
      trackInteraction('schedule_element_card_opened', {
        schedule_element_card_component,
        schedule_element_card_shift_planification_type,
        schedule_element_card_shift_status
      })
    },
    shortcutPerformed: (
      shortcut_action_name: ScheduleFeaturesShortcutActionName,
      shortcut_target_component: ScheduleFeaturesShortcutTargetComponent
    ) =>
      trackInteraction('shortcut_performed', {
        shortcut_action_name,
        shortcut_target_component
      }),
    restCreationInBulkPerformed: (
      contracts_with_shifts_to_delete_count: ScheduleFeatureRestCreationInBulkContractsWithShiftsToDeleteCount,
      contracts_with_shifts_deletion_requested_count: ScheduleFeatureRestCreationInBulkContractsWithShiftsDeletionRequestedCount
    ) =>
      trackInteraction('rest_creation_in_bulk_performed', {
        rest_creation_in_bulk_contracts_with_shifts_to_delete_count:
          contracts_with_shifts_to_delete_count,
        rest_creation_in_bulk_contracts_with_shifts_deletion_requested_count:
          contracts_with_shifts_deletion_requested_count
      })
  }
}

const determineShiftStatus = (shift_status_data?: {
  published: boolean
  declared: boolean
  validated: boolean
}): ScheduleFeatureShiftStatus | undefined => {
  if (!shift_status_data) {
    return undefined
  }

  const { published, declared, validated } = shift_status_data

  switch (true) {
    case validated:
      return 'validated'
    case declared:
      return 'declared'
    case published:
      return 'published'
    default:
      return 'unpublished'
  }
}
